import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Sparkles, CheckCircle2, Lock } from "lucide-react";
import { toast } from "sonner";
import { getTrack, claim, syncSeasonTrack } from "@/lib/seasonRewards";
import { setCurrentSkinId } from "@/lib/skins";
import { sfx } from "@/lib/sfx";
import { fireConfetti } from "@/lib/confetti";

export default function SeasonTrackDialog({ open, onOpenChange }) {
  const [track, setTrack] = useState(() => getTrack());

  useEffect(() => {
    if (open) {
      syncSeasonTrack();
      setTrack(getTrack());
    }
  }, [open]);

  const onClaim = (m) => {
    sfx.pickup();
    const claimed = claim(m.id);
    if (!claimed) return;
    // Skin milestones auto-equip the skin
    if (claimed.kind === "skin" && claimed.skinId) {
      const s = setCurrentSkinId(claimed.skinId);
      toast.success(`CLAIMED · ${claimed.label} · ${s?.name || claimed.reward}`);
    } else {
      toast.success(`CLAIMED · ${claimed.label} · ${claimed.reward}`);
    }
    // Skin confetti burst — accent color varies per milestone
    const palette = {
      champion:  { color: "#F0ABFC", glow: "rgba(240,171,252,0.9)" },
      extralife: { color: "#FF0055", glow: "rgba(255,0,85,0.9)" },
      emerald:   { color: "#39FF14", glow: "rgba(57,255,20,0.9)" },
      ruby:      { color: "#FF3355", glow: "rgba(255,51,85,0.9)" },
      diamond:   { color: "#B0F0FF", glow: "rgba(176,240,255,0.9)" },
    };
    fireConfetti({ ...palette[claimed.id] || palette.champion, count: claimed.id === "diamond" ? 120 : 60 });
    setTrack(getTrack());
  };

  const daysRemaining = () => {
    if (!track.expires_at) return null;
    const ms = new Date(track.expires_at).getTime() - Date.now();
    if (ms <= 0) return 0;
    return Math.floor(ms / 86400000);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="season-track-dialog"
        className="max-w-2xl bg-black/85 backdrop-blur-2xl border border-white/10 text-white rounded-none p-0"
      >
        <div className="p-8 border-b border-white/10 bg-gradient-to-br from-fuchsia-500/5 to-transparent">
          <DialogHeader className="text-left space-y-2">
            <div className="flex items-center gap-2 text-[10px] tracking-[0.4em] text-[#F0ABFC]">
              <Sparkles size={14} /> SEASON · REWARDS TRACK
            </div>
            <DialogTitle className="font-display text-3xl tracking-tighter text-white">
              Day <span className="text-[#F0ABFC]" style={{ textShadow: "0 0 22px rgba(240,171,252,0.6)" }}>{track.day || "—"}</span> of your season
            </DialogTitle>
            <DialogDescription className="text-white/60">
              {track.active
                ? (track.plan === "season_monthly"
                    ? "Monthly pass · perks renew each billing cycle"
                    : (daysRemaining() != null ? `${daysRemaining()} day${daysRemaining() === 1 ? "" : "s"} remaining on this pass` : "Season pass active"))
                : "Buy a Season Pass to start unlocking daily rewards."}
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="p-6 max-h-[60vh] overflow-y-auto">
          <ol className="space-y-3" data-testid="season-track-list">
            {track.milestones.map((m) => (
              <li
                key={m.id}
                data-testid={`milestone-${m.id}`}
                className="border p-4 flex items-center gap-4"
                style={{
                  borderColor: m.claimed ? "#39FF14" : m.unlocked ? "#F0ABFC" : "rgba(255,255,255,0.10)",
                  background: m.claimed ? "rgba(57,255,20,0.05)" : m.unlocked ? "rgba(240,171,252,0.04)" : "transparent",
                  boxShadow: m.unlocked && !m.claimed ? "0 0 18px rgba(240,171,252,0.2)" : "none",
                }}
              >
                <div
                  className="w-12 h-12 flex flex-col items-center justify-center border font-mono text-xs tracking-widest shrink-0"
                  style={{
                    borderColor: m.claimed ? "#39FF14" : m.unlocked ? "#F0ABFC" : "rgba(255,255,255,0.20)",
                    color: m.claimed ? "#39FF14" : m.unlocked ? "#F0ABFC" : "rgba(255,255,255,0.4)",
                  }}
                >
                  <span className="text-[9px] opacity-70">DAY</span>
                  <span className="text-lg font-bold">{m.day}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs tracking-[0.3em] text-white">{m.label}</div>
                  <div className="text-[10px] tracking-widest text-white/50 mt-1">{m.reward}</div>
                  {m.play_required > 0 && (
                    <div className="text-[10px] tracking-widest mt-1" style={{ color: m.play_ok ? "#39FF14" : "#FF9020" }}>
                      PLAY DAYS · {m.play_progress}/{m.play_required}
                    </div>
                  )}
                </div>
                {m.claimed ? (
                  <div className="flex items-center gap-1 text-[10px] tracking-[0.3em] text-[#39FF14]">
                    <CheckCircle2 size={14} /> CLAIMED
                  </div>
                ) : m.unlocked ? (
                  <button
                    data-testid={`claim-${m.id}`}
                    onClick={() => onClaim(m)}
                    className="px-4 py-2 border border-[#F0ABFC] text-[#F0ABFC] hover:bg-[#F0ABFC] hover:text-black transition-colors text-[10px] tracking-[0.3em]"
                    style={{ boxShadow: "0 0 14px rgba(240,171,252,0.35)" }}
                  >
                    CLAIM
                  </button>
                ) : !m.day_ok ? (
                  <div className="flex items-center gap-1 text-[10px] tracking-[0.3em] text-white/40">
                    <Lock size={12} /> DAY {m.day}
                  </div>
                ) : (
                  <div className="flex items-center gap-1 text-[10px] tracking-[0.3em] text-[#FF9020]">
                    <Lock size={12} /> PLAY MORE
                  </div>
                )}
              </li>
            ))}
          </ol>
          {!track.active && (
            <div className="mt-6 text-center text-[10px] tracking-[0.3em] text-white/40">
              Buy any Season Pass to unlock this track.
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
